import React, { useState } from 'react'
import { ChevronDown, ChevronRight, Check, X, Pencil, Sparkles } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { ApprovalQueueItem } from '../types'

type Props = {
  // Pre-filtered by the caller (ApprovalsSettingsSection groups one shared queue
  // fetch into per-action-type sections) rather than taking the whole queue hook —
  // this component only ever renders the list it's handed, one category at a time.
  items: ApprovalQueueItem[]
  resolvingIds: Record<string, boolean>
  resolveErrors: Record<string, string>
  approve: (itemId: string, edits?: { subject?: string; body?: string }) => Promise<void>
  dismiss: (itemId: string) => Promise<void>
  emptyMessage: string
}

// Past this many pending items, the list scrolls inside its own fixed-height panel
// instead of pushing the rest of the page down — a queue can grow past 10 in a busy
// workspace and the page shouldn't grow with it.
const SCROLL_AFTER = 4
const ITEM_ROW_ESTIMATE_PX = 96

const ApprovalQueueSection: React.FC<Props> = ({ items, resolvingIds, resolveErrors, approve, dismiss, emptyMessage }) => {
  const { currentTheme: t } = useTheme()
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({})
  const [editingIds, setEditingIds] = useState<Record<string, boolean>>({})
  const [drafts, setDrafts] = useState<Record<string, { subject: string; body: string }>>({})

  const toggleExpanded = (id: string) => setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }))

  const startEditing = (id: string, subject: string, body: string) => {
    setDrafts((prev) => ({ ...prev, [id]: prev[id] ?? { subject, body } }))
    setEditingIds((prev) => ({ ...prev, [id]: true }))
    setExpandedIds((prev) => ({ ...prev, [id]: true }))
  }

  const stopEditing = (id: string) => {
    setEditingIds((prev) => ({ ...prev, [id]: false }))
  }

  const handleApprove = (id: string, draftSubject: string, draftBody: string) => {
    const edited = editingIds[id] ? drafts[id] : undefined
    const edits =
      edited && (edited.subject !== draftSubject || edited.body !== draftBody)
        ? { subject: edited.subject, body: edited.body }
        : undefined
    void approve(id, edits)
  }

  if (items.length === 0) {
    return (
      <p className="text-[12.5px] py-2" style={{ color: t.mutedText }}>
        {emptyMessage}
      </p>
    )
  }

  return (
    <div
      className="flex flex-col gap-2"
      style={items.length > SCROLL_AFTER ? { maxHeight: SCROLL_AFTER * ITEM_ROW_ESTIMATE_PX, overflowY: 'auto' } : undefined}
    >
      {items.map((item) => {
        const isExpanded = !!expandedIds[item.id]
        const isEditing = !!editingIds[item.id]
        const isResolving = !!resolvingIds[item.id]
        const resolveError = resolveErrors[item.id]
        const draft = drafts[item.id] ?? { subject: item.draftSubject, body: item.draftBody }

        return (
          <div
            key={item.id}
            className="rounded-xl border flex flex-col"
            style={{ backgroundColor: t.sectionBackground, borderColor: t.border }}
          >
            <button
              type="button"
              onClick={() => toggleExpanded(item.id)}
              className="flex items-start justify-between gap-3 p-3.5 text-left w-full"
            >
              <div className="flex items-start gap-2.5 min-w-0">
                <span className="mt-0.5 shrink-0" style={{ color: t.mutedText }}>
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <div className="min-w-0 flex flex-col gap-0.5">
                  <span className="text-sm font-semibold truncate" style={{ color: t.text }}>
                    To: {item.to}
                  </span>
                  <span className="text-[12.5px] truncate" style={{ color: t.mutedText }}>
                    {item.draftSubject}
                  </span>
                </div>
              </div>
              <span
                className="shrink-0 flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded"
                style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
              >
                {item.draftSource === 'ai' && <Sparkles size={10} />}
                {item.draftSource === 'ai' ? 'AI Draft' : 'Template'}
              </span>
            </button>

            {isExpanded && (
              <div className="px-3.5 pb-3.5 flex flex-col gap-2.5">
                {isEditing ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold" style={{ color: t.mutedText }}>Subject</label>
                      <input
                        value={draft.subject}
                        onChange={(e) => setDrafts((prev) => ({ ...prev, [item.id]: { ...draft, subject: e.target.value } }))}
                        className="w-full text-sm px-2.5 py-1.5 rounded-lg border outline-none"
                        style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold" style={{ color: t.mutedText }}>Body</label>
                      <textarea
                        value={draft.body}
                        onChange={(e) => setDrafts((prev) => ({ ...prev, [item.id]: { ...draft, body: e.target.value } }))}
                        className="w-full min-h-[90px] text-sm px-2.5 py-1.5 rounded-lg border outline-none"
                        style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap" style={{ color: t.text }}>{item.draftBody}</p>
                )}

                {resolveError && (
                  <span className="text-[12px]" style={{ color: t.errorText }}>{resolveError}</span>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    disabled={isResolving}
                    onClick={() => handleApprove(item.id, item.draftSubject, item.draftBody)}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: t.accentGradient ?? t.accent }}
                  >
                    <Check size={13} />
                    {isEditing ? 'Save & Approve' : 'Approve'}
                  </button>
                  {!isEditing && (
                    <button
                      type="button"
                      disabled={isResolving}
                      onClick={() => startEditing(item.id, item.draftSubject, item.draftBody)}
                      className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
                    >
                      <Pencil size={13} />
                      Edit
                    </button>
                  )}
                  {isEditing && (
                    <button
                      type="button"
                      disabled={isResolving}
                      onClick={() => stopEditing(item.id)}
                      className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={isResolving}
                    onClick={() => void dismiss(item.id)}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20"
                  >
                    <X size={13} />
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default React.memo(ApprovalQueueSection)
